import { useAuth } from './useAuth'
import { useSubscription } from './useSubscription'
import { useOffersModule } from './useOffersModule'
import { useDestinationAccess } from './useDestinationAccess'
import { appNavigation, appNavigationItems, type AppNavigationItem } from '@/lib/app-navigation'
import { canUsePresentations } from '@/lib/presentations-access'

const KEY = (platform: string, userId?: string) => `er:nav:${userId ?? 'anon'}:${platform}`

/**
 * The menu's entries, steady from the first frame (sweep, 24.10.2026: the sidebar went 5 → 7 → 6
 * items on every signed-in reload, because three inputs each guessed differently while they
 * loaded). Until every input is known this returns the entries this browser showed last time;
 * the answer, once known, is remembered for the next load. One hook for the sidebar and the phone
 * menu, so the two cannot disagree.
 */
export function useAppNavigation(platform: 'desktop' | 'mobile'): AppNavigationItem[] {
  const { user } = useAuth()
  const { isPro, loading: subscriptionLoading } = useSubscription()
  const { isOperator, canAccessDestination, loading: accessLoading } = useDestinationAccess()
  const { enabled: offersEnabled, loading: offersLoading } = useOffersModule()
  const entries = appNavigation(
    { isOperator, canAccessDestination, isPro, presentations: canUsePresentations(user?.email), offersEnabled },
    platform,
  )
  const ready = !subscriptionLoading && !accessLoading && !offersLoading
  try {
    if (ready) {
      localStorage.setItem(KEY(platform, user?.id), JSON.stringify(entries.map((e) => [e.key, e.href, e.tag ?? null])))
      return entries
    }
    const remembered = JSON.parse(localStorage.getItem(KEY(platform, user?.id)) ?? 'null') as [string, string, string | null][] | null
    if (remembered) {
      const all = appNavigationItems()
      const restored = remembered.flatMap(([key, href, tag]) => {
        const item = all.find((i) => i.key === key)
        return item ? [{ ...item, href, ...(tag ? { tag } : {}) }] : []
      })
      if (restored.length) return restored
    }
  } catch { /* storage unavailable: show the live answer, as before */ }
  return entries
}
